import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Table, Select, Space, message } from 'antd';
import {
  getPurchaseCostMaterials, getPurchaseCostSuppliers, getPurchaseCostOrders,
  getPurchaseOrderLines, getPurchaseMaterialComparison,
} from '../../../../services/api';
import { materialColumns, supplierColumns, orderColumns, orderLineColumns, comparisonColumns, yuan, priceText } from '../columns';

const PAGE_SIZE = 20;
const scroll = { x: 1500 };

function usePagedList(fetcher, params, active) {
  const key = JSON.stringify(params);
  const [ state, setState ] = useState({ key, page: 1 });
  const [ list, setList ] = useState([]);
  const [ total, setTotal ] = useState(0);
  const [ loading, setLoading ] = useState(false);
  const page = state.key === key ? state.page : 1;

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    setLoading(true);
    fetcher({ ...params, page, pageSize: PAGE_SIZE })
      .then(res => {
        if (cancelled) return;
        setList(res.data.list || []);
        setTotal(res.data.total || 0);
      })
      .catch(err => { if (!cancelled) message.error(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ active, key, page, fetcher, params ]);

  return { list, total, page, setPage: p => setState({ key, page: p }), loading };
}

function PagedTable({ rowKey, columns, list, total, page, setPage, loading, footer }) {
  return (
    <>
      {footer && <div style={{ marginBottom: 8, color: '#666' }}>{footer}</div>}
      <Table
        rowKey={rowKey}
        columns={columns}
        dataSource={list}
        loading={loading}
        size="small"
        pagination={{ current: page, total, pageSize: PAGE_SIZE, onChange: setPage, showTotal: t => `共 ${t} 条` }}
        scroll={scroll}
      />
    </>
  );
}

export function MaterialsTab({ params, active }) {
  const paged = usePagedList(getPurchaseCostMaterials, params, active);
  const pageAmount = paged.list.reduce((acc, r) => acc + Number(r.amount || 0), 0);
  return (
    <PagedTable
      rowKey="materialNumber"
      columns={materialColumns}
      {...paged}
      footer={`共 ${paged.total} 种物料 · 本页合计 ${yuan(pageAmount)}`}
    />
  );
}

export function SuppliersTab({ params, active }) {
  const paged = usePagedList(getPurchaseCostSuppliers, params, active);
  const pageAmount = paged.list.reduce((acc, r) => acc + Number(r.amount || 0), 0);
  return (
    <PagedTable
      rowKey="supplierNumber"
      columns={supplierColumns}
      {...paged}
      footer={`共 ${paged.total} 家供应商 · 本页合计 ${yuan(pageAmount)}`}
    />
  );
}

export function OrdersTab({ params, active }) {
  const paged = usePagedList(getPurchaseCostOrders, params, active);
  const key = JSON.stringify(params);
  const [ linesMap, setLinesMap ] = useState({});
  const [ loadingBill, setLoadingBill ] = useState(null);

  useEffect(() => { setLinesMap({}); }, [ key ]);

  const loadLines = async billNo => {
    if (linesMap[billNo]) return;
    setLoadingBill(billNo);
    try {
      const res = await getPurchaseOrderLines(billNo);
      setLinesMap(prev => ({ ...prev, [billNo]: res.data || [] }));
    } catch (err) {
      message.error(err.message);
    }
    setLoadingBill(null);
  };

  const pageAmount = paged.list.reduce((acc, r) => acc + Number(r.totalAmount || 0), 0);
  const totalLines = paged.list.reduce((acc, r) => acc + Number(r.lineCount || 0), 0);

  return (
    <>
      <div style={{ marginBottom: 8, color: '#666' }}>共 {paged.total} 张采购单 · 本页合计 {yuan(pageAmount)}（{totalLines} 行明细）</div>
      <Table
        rowKey="billNo"
        columns={orderColumns}
        dataSource={paged.list}
        loading={paged.loading}
        size="small"
        pagination={{ current: paged.page, total: paged.total, pageSize: PAGE_SIZE, onChange: paged.setPage, showTotal: t => `共 ${t} 条` }}
        scroll={scroll}
        expandable={{
          onExpand: (expanded, record) => { if (expanded) loadLines(record.billNo); },
          expandedRowRender: record => (
            <Table
              rowKey="id"
              columns={orderLineColumns}
              dataSource={linesMap[record.billNo] || []}
              loading={loadingBill === record.billNo}
              size="small"
              pagination={false}
              scroll={{ x: 1100 }}
            />
          ),
        }}
      />
    </>
  );
}

export function ComparisonTab({ params, active, material, onMaterialChange }) {
  const key = JSON.stringify(params);
  const [ options, setOptions ] = useState([]);
  const [ searching, setSearching ] = useState(false);
  const [ data, setData ] = useState(null);
  const [ loading, setLoading ] = useState(false);
  const timerRef = useRef(null);

  const loadOptions = useCallback(async keyword => {
    setSearching(true);
    try {
      const res = await getPurchaseCostMaterials({ ...params, materialKeyword: keyword || undefined, page: 1, pageSize: 50 });
      setOptions((res.data.list || []).map(m => ({
        value: m.materialNumber,
        label: `${m.materialNumber} ${m.materialName}（${yuan(m.amount)}）`,
      })));
    } catch (err) {
      message.error(err.message);
    }
    setSearching(false);
  }, [ params ]);

  useEffect(() => {
    if (active) loadOptions('');
  }, [ active, loadOptions ]);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    setLoading(true);
    getPurchaseMaterialComparison({ ...params, materialNumber: material || undefined })
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(err => { if (!cancelled) message.error(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ active, key, params, material ]);

  const onSearch = value => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => loadOptions(value), 400);
  };

  const list = (data && data.list) || [];
  const cheapest = list.length ? list.find(r => r.diffVsMin === 0) : null;

  return (
    <>
      <Space style={{ marginBottom: 12 }} wrap>
        <span>选择物料：</span>
        <Select
          showSearch
          allowClear
          value={material}
          placeholder="默认取当前筛选下金额最高的物料"
          style={{ width: 460 }}
          options={options}
          loading={searching}
          filterOption={false}
          onSearch={onSearch}
          onChange={onSelect => onMaterialChange(onSelect)}
        />
        {data && data.materialNumber && (
          <span style={{ color: '#666' }}>
            {data.materialNumber} {data.materialName || ''}{data.unit ? ` / 单位：${data.unit}` : ''}
            {cheapest ? ` · 最低均价 ${priceText(cheapest.avgPrice)}（${cheapest.supplierName}）` : ''}
          </span>
        )}
      </Space>
      <Table
        rowKey="supplierNumber"
        columns={comparisonColumns}
        dataSource={list}
        loading={loading}
        size="small"
        pagination={false}
        scroll={scroll}
        locale={{ emptyText: '该物料在当前筛选条件下没有采购记录' }}
      />
    </>
  );
}
